import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createLogger } from '../../../shared/config/logger';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../../../shared/services/supabase/client';

const logger = createLogger('Confirm.jsx');

export default function Confirm() {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [status, setStatus] = useState({ type: 'processing', message: null, error: null });

    // Sign out immediately when component mounts to prevent auto-login
    // This must happen BEFORE processing the confirmation to prevent redirect to dashboard
    useEffect(() => {
        const signOutImmediately = async () => {
            try {
                logger.info('Signing out user immediately on email confirmation page');
                await supabase.auth.signOut();
                // Also clear Redux state
                try {
                    await logout();
                } catch (logoutError) {
                    logger.warn('Error clearing Redux state', logoutError);
                }
            } catch (signOutError) {
                logger.warn('Error signing out on confirm page', signOutError);
            }
        };
        
        signOutImmediately();
    }, [logout]);

    useEffect(() => {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);

        const error = hashParams.get('error') || queryParams.get('error');
        const errorDescription =
            hashParams.get('error_description') || queryParams.get('error_description');

        if (error) {
            logger.error('Confirmation error detected', { error, errorDescription });
            setStatus({
                type: 'error',
                message: null,
                error: `${error}: ${errorDescription}`,
            });
            setTimeout(() => navigate('/'), 3000);
        } else {
            logger.info('Email confirmation successful');
            setStatus({
                type: 'success',
                message: 'Email confirmed successfully! Please log in.',
                error: null,
            });
            setTimeout(() => navigate('/login'), 3000);
        }
    }, [navigate]);

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <div className="max-w-md w-full mx-4">
                <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                    {status.type === 'processing' && (
                        <>
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing confirmation...</h2>
                            <p className="text-gray-600">Please wait while we verify your email.</p>
                        </>
                    )}

                    {status.type === 'success' && (
                        <>
                            <div className="flex justify-center mb-4">
                                <div className="rounded-full bg-green-100 p-3">
                                    <CheckCircleIcon className="h-12 w-12 text-green-600" />
                                </div>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">Success!</h2>
                            <p className="text-gray-600 mb-4">{status.message}</p>
                            <p className="text-sm text-gray-500">Redirecting to login page...</p>
                        </>
                    )}

                    {status.type === 'error' && (
                        <>
                            <div className="flex justify-center mb-4">
                                <div className="rounded-full bg-red-100 p-3">
                                    <XCircleIcon className="h-12 w-12 text-red-600" />
                                </div>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">Confirmation Failed</h2>
                            <p className="text-gray-600 mb-4">{status.error}</p>
                            <p className="text-sm text-gray-500 mb-4">Invalid confirmation link. Redirecting to home page...</p>
                            <button
                                onClick={() => navigate('/')}
                                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Go to Home
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
